import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { featureCollection, intersect as turfIntersect, polygon as turfPolygon } from '@turf/turf';
import { GeoJSON, GeoJsonProperties } from 'geojson';
import {
    AddLayerObject,
    ExpressionFilterSpecification,
    FilterSpecification,
    GeoJSONSource,
    LayerSpecification,
    LngLatBoundsLike,
} from 'maplibre-gl';
import { map as radashMap } from 'radash';
import { firstValueFrom } from 'rxjs';

import { MapBaseSource, MapLayerSource } from '@connect-services/client/components/map/map.component';
import { BYPASS_INTERCEPTOR } from '@connect-services/client/interceptors/http-errors.interceptor';
import { GeomLayer } from '@connect-services/client/models/geom-layer.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { GeohashService } from '@connect-services/client/services/geohash.service';
import { GeometryEditPreviewService } from '@connect-services/client/services/geometry-edit-preview.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapLayerStorageService } from '@connect-services/client/services/map-layer-storage.service';
import { DrawSourcesOptions, MapSourceService } from '@connect-services/client/services/map-source.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { WfsRequestService } from '@connect-services/client/services/wfs-request.service';
import {
    EditAction,
    EntityState,
    Feature,
    FeatureCollection,
    Layer,
    LayersService,
    MapboxStyle,
} from '@connect-services/src/sdk/api';

type BboxGeohash = {
    [k: string]: ReturnType<typeof turfPolygon<GeoJsonProperties>>;
};

type LayerSpecificationWithType<T extends LayerSpecification['type']> = Extract<AddLayerObject, { type: T }>;

@Injectable({
    providedIn: 'root',
})
export class MapLayerService extends MapBaseService {
    public readonly areLayersVisible = signal<boolean>(true);

    private readonly editModStateService = inject(EditModeStateService);
    private readonly geohashService = inject(GeohashService);
    private readonly geometryEditPreviewService = inject(GeometryEditPreviewService);
    private readonly layersService = inject(LayersService);
    private readonly mapLayerStorageService = inject(MapLayerStorageService);
    private readonly sourcesService = inject(SourcesService);
    private readonly wfsRequestService = inject(WfsRequestService);

    public async disableLayersZoom(layers: Layer[]): Promise<MapBaseSource<unknown>[]> {
        const sourcesWithZoom: MapLayerSource[] = await radashMap(layers, async (layer) => {
            const style = await this.sourcesService.getLayerStyle(layer.layerStyle);

            return new MapLayerSource(GeomLayer.fromLayer(layer), style, true);
        });

        return this.removeAllZoom(sourcesWithZoom);
    }

    public draw(
        sourceId: string,
        geojson: GeoJSON,
        bbox: LngLatBoundsLike,
        style: MapboxStyle[],
        isVisible: boolean,
        beforeId?: string,
    ): void {
        this.addGeoJSONSource(sourceId, geojson as GeoJSON);
        this.addBaseSourceLayer(sourceId, beforeId);
        this.addSourceStyle(sourceId, style, beforeId);
        this.setSourceVisibility(sourceId, style, isVisible);

        if (this.mapBounds && isVisible) {
            this.mapBounds.extend(bbox);
        }
    }

    public async drawLayer(
        source: MapLayerSource,
        options: DrawSourcesOptions,
        isEditModeActive: boolean = false,
    ): Promise<void> {
        if (!this.map) {
            return;
        }
        const isSourceAlreadyInitialized = !!this.map.getSource(source.sourceId);
        const areLayersVisible = isSourceAlreadyInitialized ? this.isSourceVisible(source) : source.isVisible;

        switch (source.data.type) {
            case Layer.TypeEnum.GEOM: {
                this.drawGeomLayer(source.sourceId, source.style, areLayersVisible, source.data, options);
                break;
            }
            case Layer.TypeEnum.REFERENCE_DATA:
            case Layer.TypeEnum.INTERSTANDARD:
            case Layer.TypeEnum.TABLE: {
                await this.drawTableLayer(
                    source.sourceId,
                    source.style,
                    this.isZoomIncluded(source.maxzoom, source.minzoom) && !source.isFiltered,
                    areLayersVisible,
                    source.data,
                    options,
                    false,
                    isEditModeActive,
                );
                break;
            }
            case Layer.TypeEnum.WFS: {
                this.drawWfsLayer(source, options);
                break;
            }
            case Layer.TypeEnum.WMS: {
                this.drawWmsLayer(source, options);
                break;
            }
            case Layer.TypeEnum.WMTS: {
                break;
            }
        }
    }

    public async drawTableLayer(
        id: string,
        style: MapboxStyle[],
        isActivated: boolean,
        isVisible: boolean,
        layer: Layer,
        options: DrawSourcesOptions,
        onlyPolygons: boolean = false,
        isEditModeActive: boolean = false,
    ): Promise<void> {
        if (!this.isMapPresent()) {
            return;
        }

        let geojson: FeatureCollection;
        this.addBaseSourceLayer(id, options.beforeId);

        if (!this.isMapPresent()) {
            return;
        }

        if (isActivated) {
            if (options.geohashes) {
                const geohashesBbox: BboxGeohash = options.geohashes.reduce((acc: BboxGeohash, geohash: string) => {
                    return {
                        ...acc,
                        [geohash]: turfPolygon(this.geohashService.getGeohashBbox(geohash).coordinates),
                    };
                }, {});

                const mapBounds = this.map!.getBounds();
                const mapBbox = turfPolygon([
                    [
                        [mapBounds.getNorthWest().lng, mapBounds.getNorthWest().lat],
                        [mapBounds.getNorthEast().lng, mapBounds.getNorthEast().lat],
                        [mapBounds.getSouthEast().lng, mapBounds.getSouthEast().lat],
                        [mapBounds.getSouthWest().lng, mapBounds.getSouthWest().lat],
                        [mapBounds.getNorthWest().lng, mapBounds.getNorthWest().lat],
                    ],
                ]);

                await this.loadMissingGeohashes(layer, options, geohashesBbox, mapBbox);

                geojson = this.mapLayerStorageService.getGeojson(layer.id, options.geohashes);
            } else {
                try {
                    geojson = await firstValueFrom(
                        this.layersService.layerControllerGetEntitiesGeoJson(
                            {
                                excludeProperties: true,
                                id: layer.id,
                                intersectsFieldInterventionArea: options.intersectsFieldInterventionArea,
                                onlyPolygons: onlyPolygons,
                                zoneDelimiter: options?.zoneDelemiter,
                            },
                            'body',
                            true,
                            {
                                context: new HttpContext().set(BYPASS_INTERCEPTOR, ['httpErrorsInterceptor']),
                            },
                        ),
                    );
                } catch {
                    return;
                }
            }

            if (isEditModeActive) {
                geojson = this.mergeLocalStorageAndBaseFeatures(layer.project, layer.id, geojson);
            }

            if (!geojson.features.length) {
                return;
            }

            this.draw(id, geojson as GeoJSON, geojson.bbox as LngLatBoundsLike, style, isVisible, options.beforeId);
        }
    }

    public async filterSourceLayersByPropertyValues(
        sourceId: string,
        propertyName: string,
        values: string[],
    ): Promise<void> {
        if (!this.map) {
            return;
        }

        if (!this.map.isStyleLoaded()) {
            await new Promise<void>((resolve) => {
                this.map!.once('idle', () => resolve());
            });
        }

        if (!this.map.getSource(sourceId)) {
            return;
        }

        const layerIds = this.map.getStyle().layers.reduce<string[]>((acc: string[], layer: LayerSpecification) => {
            if (!('source' in layer) || layer.source !== sourceId || !this.map?.getLayer(layer.id)) {
                return acc;
            }

            acc.push(layer.id);

            return acc;
        }, []);

        if (!layerIds.length) {
            return;
        }

        const filter: ExpressionFilterSpecification = ['in', ['get', propertyName], ['literal', values]];

        layerIds.forEach((layerId: string) => {
            this.map?.setFilter(layerId, filter);
        });

        this.map.redraw();
    }

    public getDefaultFilter(type: MapboxStyle.TypeEnum): string[] | undefined {
        switch (type) {
            case MapboxStyle.TypeEnum.SYMBOL:
                return ['==', '$type', 'Point'];
            case MapboxStyle.TypeEnum.LINE:
                return ['==', '$type', 'LineString'];
            case MapboxStyle.TypeEnum.FILL:
                return ['==', '$type', 'Polygon'];
            default:
                return undefined;
        }
    }

    public getMapSource(sourceId: string): GeoJSONSource | undefined {
        return this.map?.getSource<GeoJSONSource>(sourceId);
    }

    public isLayerCurrentlyVisible(layerId: string): boolean {
        const source: MapBaseSource<unknown> | undefined = this.sourcesService
            .sources()
            .find((mapSource: MapBaseSource<unknown>) => mapSource.sourceId === layerId);

        const isZoomIncluded = this.isZoomIncluded(source?.maxzoom, source?.minzoom);

        if (!(source instanceof MapLayerSource) || !isZoomIncluded) {
            return false;
        }

        if (!MapSourceService.isSourceRenderable(source)) {
            return false;
        }

        return this.isSourceVisible(source);
    }

    public isSourceVisible(source: MapLayerSource): boolean {
        if (!this.isMapPresent()) {
            return false;
        }

        if (source.data.type === Layer.TypeEnum.WMS) {
            const layerId = this.createWmsLayerId(source);
            if (!this.map.getLayer(layerId)) {
                return false;
            }

            return this.map.getLayoutProperty(layerId, 'visibility') === 'visible';
        }

        return source.style.every((style: MapboxStyle) => {
            const layerId = this.createLayerId(source.sourceId, style.id);

            if (!this.map.getLayer(layerId)) {
                return false;
            }

            return this.map.getLayoutProperty(layerId, 'visibility') === 'visible';
        });
    }

    public layerExists(id: string): boolean {
        return !!this.map?.getLayer(id);
    }

    public previewLayerStyle(
        source: MapBaseSource<unknown>,
        style: MapboxStyle[],
        forceVisibility: boolean,
        disableZoom?: boolean,
    ): void {
        if (!this.map?.getSource(source.sourceId)) {
            return;
        }

        const mapStyle = this.map?.getStyle();
        mapStyle?.layers.forEach((layerStyle) => {
            if (!layerStyle.id.startsWith(source.sourceId)) {
                return;
            }
            this.map?.removeLayer(layerStyle.id);
        });

        let visibility = 'visible';
        if (!forceVisibility) {
            visibility = source.isVisible ? 'visible' : 'none';
        }

        style.forEach((sourceStyle: MapboxStyle) => {
            this.addStyleLayerToMap(sourceStyle, visibility, source, !!disableZoom);
        });
    }

    public removeAllZoom(sources: MapBaseSource<unknown>[]): MapBaseSource<unknown>[] {
        if (!sources?.length) {
            return [];
        }

        return sources.map((source: MapBaseSource<unknown>) => {
            return this.removeSourceZoom(source);
        });
    }

    public override setSourceVisibility(sourceId: string, style: MapboxStyle[], isVisible: boolean): void {
        if (!this.map) {
            return;
        }

        super.setSourceVisibility(sourceId, style, isVisible);
    }

    public setSourceVisibilityWithoutStyle(source: MapLayerSource, isVisible: boolean): void {
        source.isVisible = isVisible;

        if (!this.map) {
            return;
        }

        if (source.data.type === Layer.TypeEnum.WMS) {
            source.isVisible = this.map.getLayoutProperty(this.createWmsLayerId(source), 'visibility') !== 'visible';
            this.map.setLayoutProperty(
                this.createWmsLayerId(source),
                'visibility',
                source.isVisible ? 'visible' : 'none',
            );

            return;
        }

        for (const style of source.style) {
            const layerId = this.createLayerId(source.sourceId, style.id);

            if (!this.map.getLayer(layerId)) {
                continue;
            }

            this.map.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none');
        }
    }

    public override toggleSourceVisibility(source: MapLayerSource): void {
        if (!this.map) {
            return;
        }

        if (source.data.type === Layer.TypeEnum.WMS) {
            source.isVisible = this.map.getLayoutProperty(this.createWmsLayerId(source), 'visibility') !== 'visible';
            this.map.setLayoutProperty(
                this.createWmsLayerId(source),
                'visibility',
                source.isVisible ? 'visible' : 'none',
            );

            return;
        }

        super.toggleSourceVisibility(source);
    }

    private addStyleLayerToMap(
        sourceStyle: MapboxStyle,
        visibility: string,
        source: MapBaseSource<unknown>,
        disableZoom: boolean,
    ): void {
        sourceStyle.layout = {
            ...sourceStyle.layout,
            visibility,
        };

        const layerType = this.getType(sourceStyle.type);
        const filter =
            (sourceStyle.filter as FilterSpecification) ??
            this.getDefaultFilter(sourceStyle.type ?? MapboxStyle.TypeEnum.SYMBOL);

        const layerSpecification: LayerSpecificationWithType<typeof layerType> = {
            ...sourceStyle,
            filter,
            id: `${source.sourceId}-${sourceStyle.id}`,
            maxzoom: sourceStyle.maxzoom ?? undefined,
            minzoom: sourceStyle.minzoom ?? undefined,
            source: source.sourceId,
            type: layerType,
        };

        if (!layerSpecification.minzoom || !layerSpecification.maxzoom || disableZoom) {
            delete layerSpecification.minzoom;
            delete layerSpecification.maxzoom;
        }
        this.map?.addLayer(layerSpecification);
    }

    private applyGeometryEdits(features: Feature[], stateByEntityId: Map<string, EntityState>): Feature[] {
        if (!features.length) {
            return [];
        }

        return features.reduce((acc: Feature[], feature: Feature) => {
            const entityId = this.getFeatureUuid(feature);
            const state = stateByEntityId.get(entityId);

            const copiedFeature = structuredClone(feature);
            copiedFeature.properties = {
                ...copiedFeature.properties,
                ...state?.properties,
            };

            if (state?.businessIdentifier) {
                AssertService.hasKey(state?.businessIdentifier, 'key');
                AssertService.hasKey(state?.businessIdentifier, 'value');
                AssertService.isString(state.businessIdentifier['key']);

                copiedFeature.properties = {
                    ...copiedFeature.properties,
                    businessIdentifier:
                        state?.properties?.[state.businessIdentifier['key']] ?? state.businessIdentifier['value'],
                };
            }

            if (!state?.properties?.['geom']) {
                acc.push(copiedFeature);

                return acc;
            }

            copiedFeature.geometry = state.properties['geom'];

            acc.push(copiedFeature);

            return acc;
        }, []);
    }

    private applyPreviewGeometryEdits(features: Feature[], layerId: string): Feature[] {
        if (!features.length) {
            return [];
        }

        return features.map((feature: Feature) => {
            const entityId = this.getFeatureUuid(feature);
            const previewGeometry = this.geometryEditPreviewService.getGeometry(layerId, entityId);

            if (!previewGeometry) {
                return feature;
            }

            const copiedFeature = structuredClone(feature);
            copiedFeature.geometry = previewGeometry as Feature['geometry'];

            return copiedFeature;
        });
    }

    private buildCreatedFeatures(layerState: EntityState[]): Feature[] {
        if (!layerState.length) {
            return [];
        }

        return layerState
            .filter((entityState) => entityState.action === EditAction.CREATE && !!entityState.properties?.['geom'])
            .map((entityState) => {
                return {
                    geometry: entityState.properties!['geom'],
                    properties: { ...entityState.properties },
                    type: Feature.TypeEnum.FEATURE,
                };
            });
    }

    private createWmsLayerId(source: MapLayerSource): string {
        return `${source.name}-${source.sourceId}`;
    }

    private drawGeomLayer(
        id: string,
        style: MapboxStyle[],
        isVisible: boolean,
        layer: Layer,
        options: DrawSourcesOptions,
    ): void {
        if (!this.isMapPresent()) {
            return;
        }

        AssertService.assertGeometryType(layer.geom);

        this.draw(id, layer.geom as GeoJSON, layer.geom.bbox as LngLatBoundsLike, style, isVisible, options.beforeId);
    }

    private drawWfsLayer(source: MapLayerSource, options: DrawSourcesOptions): void {
        const layer: Layer = source.data;
        if (!this.isMapPresent()) {
            return;
        }

        if (this.map.getSource(source.sourceId)) {
            return;
        }

        this.addBaseSourceLayer(source.sourceId, options.beforeId);

        const subscription = this.wfsRequestService.getWfsData(layer)?.subscribe((response) => {
            if (response instanceof HttpErrorResponse) {
                return;
            }

            this.map.addSource(source.sourceId, {
                data: response as GeoJSON,
                type: 'geojson',
            });

            this.addSourceStyle(source.sourceId, source.style, options.beforeId);
            this.setSourceVisibility(source.sourceId, source.style, source.isVisible);
        });

        if (!subscription) {
            return;
        }

        this.wfsRequestService.wfsSubscriptions.push(subscription);
    }

    private drawWmsLayer(source: MapLayerSource, options: DrawSourcesOptions): void {
        const layer: Layer = source.data;
        if (!this.isMapPresent()) {
            return;
        }

        if (!this.map.getSource(source.sourceId)) {
            this.map.addSource(source.sourceId, {
                tileSize: 256,
                tiles: [layer.url!], // use the tiles option to specify a WMS tile source URL
                type: 'raster',
            });
        }

        this.addBaseSourceLayer(source.sourceId, options.beforeId);

        const layerId = this.createWmsLayerId(source);

        if (!this.map.getLayer(layerId)) {
            this.map.addLayer(
                {
                    id: layerId,
                    layout: { visibility: source.isVisible ? 'visible' : 'none' },
                    paint: { 'raster-opacity': 0.7 },
                    source: layer.id,
                    type: 'raster',
                },
                options.beforeId,
            );
        } else {
            this.map.moveLayer(layerId, options.beforeId);
            this.map.setLayoutProperty(layerId, 'visibility', source.isVisible ? 'visible' : 'none');
        }
    }

    private filterOutDeletedFeatures(features: Feature[], stateByEntityId: Map<string, EntityState>): Feature[] {
        if (!features.length) {
            return [];
        }

        return features.filter((feature: Feature) => {
            const entityId = this.getFeatureUuid(feature);
            const state = stateByEntityId.get(entityId);

            return state?.action !== EditAction.DELETE;
        });
    }

    private getFeatureUuid(feature: Feature): string {
        AssertService.hasKey(feature.properties, 'uuid');
        AssertService.isString(feature.properties['uuid']);

        return feature.properties['uuid'];
    }

    private getType(type: MapboxStyle.TypeEnum): 'symbol' | 'fill' | 'line' {
        switch (type) {
            case MapboxStyle.TypeEnum.LINE:
                return 'line';
            case MapboxStyle.TypeEnum.FILL:
                return 'fill';
            default:
                return 'symbol';
        }
    }

    private async loadMissingGeohashes(
        layer: Layer,
        options: DrawSourcesOptions,
        geohashesBbox: BboxGeohash,
        mapBbox: ReturnType<typeof turfPolygon<GeoJsonProperties>>,
    ): Promise<void> {
        if (!options.geohashes) {
            return;
        }

        const layerBbox = layer.bbox ? turfPolygon(layer.bbox.coordinates) : null;

        await radashMap(options.geohashes, async (geohash: string) => {
            if (
                this.mapLayerStorageService.isGeohashFetching(layer.id, geohash) ||
                !layerBbox ||
                !turfIntersect(featureCollection([layerBbox, geohashesBbox[geohash]])) ||
                !turfIntersect(featureCollection([mapBbox, geohashesBbox[geohash]]))
            ) {
                return;
            }

            try {
                this.mapLayerStorageService.setGeohashFetching(layer.id, geohash);
                const response = await firstValueFrom(
                    this.layersService.layerControllerGetEntitiesGeoJson(
                        {
                            excludeProperties: true,
                            geohashes: [geohash],
                            id: layer.id,
                        },
                        'body',
                        true,
                        {
                            context: new HttpContext().set(BYPASS_INTERCEPTOR, ['httpErrorsInterceptor']),
                        },
                    ),
                );
                this.mapLayerStorageService.storeGeohashForLayer(layer.id, geohash, response);
            } catch (err: unknown) {
                if (err instanceof HttpErrorResponse && err.status === 404) {
                    this.mapLayerStorageService.storeGeohashForLayer(layer.id, geohash, null);
                }
            }
        });
    }

    private mergeLocalStorageAndBaseFeatures(
        projectId: string,
        layerId: string,
        featureCollection: FeatureCollection,
    ): FeatureCollection {
        const layerState = this.editModStateService.getLayerState(projectId, layerId);
        const stateByEntityId = new Map<string, EntityState>(
            layerState.map((entityState: EntityState) => [entityState.entityId, entityState]),
        );

        const baseFeatures: Feature[] = this.filterOutDeletedFeatures(featureCollection.features, stateByEntityId);
        const updatedFeatures: Feature[] = this.applyPreviewGeometryEdits(
            this.applyGeometryEdits(baseFeatures, stateByEntityId),
            layerId,
        );
        const createdFeatures: Feature[] = this.buildCreatedFeatures(layerState);

        return { ...featureCollection, features: [...updatedFeatures, ...createdFeatures] };
    }

    private removeSourceZoom(source: MapBaseSource<unknown>): MapBaseSource<unknown> {
        const copiedSource = { ...source };

        delete copiedSource.minzoom;
        delete copiedSource.maxzoom;

        if (!Array.isArray(copiedSource.style) || !copiedSource.style.length) {
            return {
                ...copiedSource,
            };
        }

        const newStyle = source.style.map((style: MapboxStyle) => {
            const copiedStyle = { ...style };

            delete copiedStyle.minzoom;
            delete copiedStyle.maxzoom;

            return copiedStyle;
        });

        return {
            ...copiedSource,
            style: newStyle,
        };
    }
}
